import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { FiPlus, FiEdit, FiTrash2, FiCalendar, FiBook, FiUsers, FiDollarSign, FiCheck, FiX, FiSettings, FiTag } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import AdminNav from '../components/AdminNav';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    duration: 60,
    price: 0,
    level: 'beginner',
    category: '',
    maxStudents: 1
  });

  const [availabilityForm, setAvailabilityForm] = useState({
    date: '',
    startTime: '',
    endTime: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [coursesRes, bookingsRes, availabilityRes, categoriesRes] = await Promise.all([
        api.get('/courses', { params: { isActive: 'all' } }),
        api.get('/bookings/admin/all'),
        api.get('/availability/admin'),
        api.get('/categories')
      ]);
      setCourses(coursesRes.data);
      setBookings(bookingsRes.data);
      setAvailability(availabilityRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Course Management
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      await api.post('/courses', courseForm);
      toast.success('Course created successfully');
      setShowCourseModal(false);
      resetCourseForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create course');
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/courses/${editingCourse.id}`, courseForm);
      toast.success('Course updated successfully');
      setShowCourseModal(false);
      setEditingCourse(null);
      resetCourseForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await api.delete(`/courses/${id}`);
      toast.success('Course deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      description: course.description,
      duration: course.duration,
      price: course.price,
      level: course.level,
      category: course.category,
      maxStudents: course.maxStudents
    });
    setShowCourseModal(true);
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      duration: 60,
      price: 0,
      level: 'beginner',
      category: '',
      maxStudents: 1
    });
  };

  // Availability Management
  const handleCreateAvailability = async (e) => {
    e.preventDefault();
    try {
      await api.post('/availability', availabilityForm);
      toast.success('Availability slot created');
      setShowAvailabilityModal(false);
      setAvailabilityForm({ date: '', startTime: '', endTime: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create availability');
    }
  };

  const handleDeleteAvailability = async (id) => {
    if (!window.confirm('Delete this availability slot?')) return;
    try {
      await api.delete(`/availability/${id}`);
      toast.success('Availability deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  // Booking Management
  const handleUpdateBookingStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}`, { status });
      toast.success(`Booking ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleAddZoomLink = async (id) => {
    const zoomLink = window.prompt('Enter Zoom meeting link:');
    if (!zoomLink) return;
    
    try {
      await api.put(`/bookings/${id}`, { zoomLink });
      toast.success('Zoom link added');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add Zoom link');
    }
  };

  // Statistics
  const totalRevenue = bookings
    .filter(b => b.paymentStatus === 'paid')
    .reduce((sum, b) => sum + parseFloat(b.amount), 0);

  const upcomingBookings = bookings.filter(b => 
    b.status === 'confirmed' && new Date(b.bookingDate) >= new Date()
  ).length;

  if (loading) return <LoadingSpinner fullScreen />;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage courses, bookings, and availability</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/admin/settings"
              className="bg-white border-2 border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:border-primary-500 transition flex items-center space-x-2"
            >
              <FiSettings />
              <span>Site Settings</span>
            </Link>
            <Link
              to="/admin/courses"
              className="bg-white border-2 border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:border-primary-500 transition flex items-center space-x-2"
            >
              <FiBook />
              <span>Courses</span>
            </Link>
            <Link
              to="/admin/availability"
              className="bg-white border-2 border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:border-primary-500 transition flex items-center space-x-2"
            >
              <FiCalendar />
              <span>Availability</span>
            </Link>
            <Link
              to="/admin/categories"
              className="bg-white border-2 border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:border-primary-500 transition flex items-center space-x-2"
            >
              <FiTag />
              <span>Categories</span>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-green-600">${totalRevenue.toFixed(2)}</p>
              </div>
              <FiDollarSign className="text-4xl text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                <p className="text-3xl font-bold text-primary-600">{bookings.length}</p>
              </div>
              <FiCalendar className="text-4xl text-primary-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Upcoming</p>
                <p className="text-3xl font-bold text-blue-600">{upcomingBookings}</p>
              </div>
              <FiUsers className="text-4xl text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Courses</p>
                <p className="text-3xl font-bold text-purple-600">{courses.length}</p>
              </div>
              <FiBook className="text-4xl text-purple-500" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md mb-8">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {['overview', 'courses', 'bookings', 'availability'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-semibold capitalize whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-b-2 border-primary-500 text-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Recent Bookings</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Course</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {bookings.slice(0, 10).map(booking => (
                        <tr key={booking.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            {booking.user.firstName} {booking.user.lastName}
                          </td>
                          <td className="px-4 py-3 text-sm">{booking.course.title}</td>
                          <td className="px-4 py-3 text-sm">
                            {format(parseISO(booking.bookingDate), 'MMM d, yyyy')}
                          </td>
                          <td className="px-4 py-3 text-sm">{booking.startTime.slice(0, 5)}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold">${booking.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Manage Courses</h2>
                  <button
                    onClick={() => {
                      setEditingCourse(null);
                      resetCourseForm();
                      setShowCourseModal(true);
                    }}
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
                  >
                    <FiPlus /> <span>Add Course</span>
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {courses.map(course => (
                    <div key={course.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{course.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{course.category}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditCourse(course)}
                            className="text-primary-500 hover:text-primary-600"
                          >
                            <FiEdit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteCourse(course.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <FiTrash2 size={20} />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm mb-4 line-clamp-2">{course.description}</p>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{course.duration} min</span>
                        <span className="font-bold text-primary-600">${course.price}</span>
                      </div>
                      <div className="mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          course.level === 'beginner' ? 'bg-green-100 text-green-800' :
                          course.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {course.level}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">All Bookings</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Course</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date & Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {bookings.map(booking => (
                        <tr key={booking.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            {booking.user.firstName} {booking.user.lastName}
                          </td>
                          <td className="px-4 py-3 text-sm">{booking.user.email}</td>
                          <td className="px-4 py-3 text-sm">{booking.course.title}</td>
                          <td className="px-4 py-3 text-sm">
                            {format(parseISO(booking.bookingDate), 'MMM d, yyyy')} {booking.startTime.slice(0, 5)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              {booking.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                                    className="text-green-600 hover:text-green-700"
                                    title="Confirm"
                                  >
                                    <FiCheck size={18} />
                                  </button>
                                  <button
                                    onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                                    className="text-red-600 hover:text-red-700"
                                    title="Cancel"
                                  >
                                    <FiX size={18} />
                                  </button>
                                </>
                              )}
                              {booking.status === 'confirmed' && !booking.zoomLink && (
                                <button
                                  onClick={() => handleAddZoomLink(booking.id)}
                                  className="text-primary-600 hover:text-primary-700 text-xs"
                                >
                                  Add Zoom
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Availability Tab */}
            {activeTab === 'availability' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Manage Availability</h2>
                  <button
                    onClick={() => setShowAvailabilityModal(true)}
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
                  >
                    <FiPlus /> <span>Add Slot</span>
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Start Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">End Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {availability.map(slot => (
                        <tr key={slot.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{slot.date}</td>
                          <td className="px-4 py-3 text-sm">{slot.startTime}</td>
                          <td className="px-4 py-3 text-sm">{slot.endTime}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              slot.isBooked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {slot.isBooked ? 'Booked' : 'Available'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {!slot.isBooked && (
                              <button
                                onClick={() => handleDeleteAvailability(slot.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <FiTrash2 size={18} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Course Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingCourse ? 'Edit Course' : 'Add New Course'}
            </h2>
            <form onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  required
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={courseForm.duration}
                    onChange={(e) => setCourseForm({ ...courseForm, duration: parseInt(e.target.value) })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={courseForm.price}
                    onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                  <select
                    value={courseForm.level}
                    onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={courseForm.category}
                    onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    {categories.filter(c => c.isActive).map(cat => (
                      <option key={cat.id} value={cat.name}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Manage categories in <Link to="/admin/categories" className="text-primary-500 hover:text-primary-600">Categories</Link> section
                  </p>
                </div>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 transition font-semibold"
                >
                  {editingCourse ? 'Update' : 'Create'} Course
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCourseModal(false);
                    setEditingCourse(null);
                    resetCourseForm();
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Availability Modal */}
      {showAvailabilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Add Availability Slot</h2>
            <form onSubmit={handleCreateAvailability} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={availabilityForm.date}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, date: e.target.value })}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={availabilityForm.startTime}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, startTime: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={availabilityForm.endTime}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, endTime: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 transition font-semibold"
                >
                  Add Slot
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAvailabilityModal(false);
                    setAvailabilityForm({ date: '', startTime: '', endTime: '' });
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

